/**
 * WebMCPStateProvider — bridge between WebMCP tools and the Cogo runtime StateManager.
 *
 * Provides read-only access to application state so tools can inspect
 * current state, page state, and resolve symbol expressions (#{...}).
 */

/**
 * Minimal StateManager interface — matches the public API of
 * `packages/cogo-runtime-core/src/state-manager.ts` without importing React deps.
 */
export interface StateManagerLike {
  get(path: string): unknown
  setState(path: string, value: unknown): void
  resolve(template: string): unknown
  getSymbolStore?(): { getSnapshot(): Record<string, unknown> }
}

export class WebMCPStateProvider {
  private stateManager: StateManagerLike

  constructor(stateManager: StateManagerLike) {
    this.stateManager = stateManager
  }

  /** Get a snapshot of the full state tree (top-level keys). */
  getState(): Record<string, unknown> {
    // StateManager stores state in a SymbolStore keyed by dot-paths.
    // We expose a proxy that delegates get() calls.
    return new Proxy({} as Record<string, unknown>, {
      get: (_target, prop) => {
        if (typeof prop === "string") {
          return this.stateManager.get(prop)
        }
        return undefined
      },
      ownKeys: () => [],
      getOwnPropertyDescriptor: () => ({
        configurable: true,
        enumerable: true,
      }),
    })
  }

  /** Get state for a specific page/block namespace. */
  getPageState(namespace: string): unknown {
    return this.stateManager.get(namespace)
  }

  /** Resolve a #{...} symbol expression against current state. */
  resolveSymbol(expression: string): unknown {
    return this.stateManager.resolve(expression)
  }

  /** Get a value at a specific state path. */
  get(path: string): unknown {
    return this.stateManager.get(path)
  }

  /** Set a value at a specific state path (for tool mutations). */
  setState(path: string, value: unknown): void {
    this.stateManager.setState(path, value)
  }

  /** Get a JSON-serializable snapshot of the full state tree. */
  getStateSnapshot(): Record<string, unknown> {
    if (this.stateManager.getSymbolStore) {
      return this.stateManager.getSymbolStore().getSnapshot()
    }
    // Fallback: return empty object (Proxy-based getState is not serializable)
    return {}
  }
}
